// [ START OF MODULE - VER 1.3 (src/utils/OfflineCacheService.js) ]
/**
 * TIGER EYE — OFFLINE CACHE SERVICE
 *
 * IndexedDB wrapper providing:
 *   - Work order data cache (active missions + current WO details)
 *   - Photo blob queue (take photos offline, upload on reconnect)
 *   - Note queue (write notes offline, sync on reconnect)
 *   - Profile cache (signed-in specialist profile + avatar bytes, offline-safe)
 *
 * DB name : tecc_offline
 * Stores  : work_orders | photo_queue | note_queue | profile
 */

const DB_NAME    = 'tecc_offline';
const DB_VERSION = 2; // v2: added 'profile' store (RMI-2026-177)

// ── Open / upgrade ────────────────────────────────────────────────────────────
const openDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, DB_VERSION);

  req.onupgradeneeded = (e) => {
    const db = e.target.result;

    // Work orders — keyed by WO id, stores the full WO object
    if (!db.objectStoreNames.contains('work_orders')) {
      db.createObjectStore('work_orders', { keyPath: 'id' });
    }

    // Photo queue — each entry: { id, wo_id, entity_type, entity_id, blob, filename, mime_type, notes, queued_at }
    if (!db.objectStoreNames.contains('photo_queue')) {
      const ps = db.createObjectStore('photo_queue', { keyPath: 'id' });
      ps.createIndex('by_entity', 'entity_id', { unique: false });
    }

    // Note queue — each entry: { id, wo_id, notes, queued_at }
    if (!db.objectStoreNames.contains('note_queue')) {
      db.createObjectStore('note_queue', { keyPath: 'id' });
    }

    // Profile — single record keyed 'me': the signed-in specialist profile + avatar Blob.
    // Added v2 (RMI-2026-177) so the mobile Profile modal renders fully offline.
    if (!db.objectStoreNames.contains('profile')) {
      db.createObjectStore('profile', { keyPath: 'id' });
    }
  };

  req.onsuccess = () => resolve(req.result);
  req.onerror   = () => reject(req.error);
});

// ── Generic helpers ───────────────────────────────────────────────────────────
const tx = (db, stores, mode = 'readonly') =>
  db.transaction(Array.isArray(stores) ? stores : [stores], mode);

const promisify = (req) => new Promise((res, rej) => {
  req.onsuccess = () => res(req.result);
  req.onerror   = () => rej(req.error);
});

// ── Work Order Cache ──────────────────────────────────────────────────────────

/**
 * Replace the entire active-missions cache for a tech.
 * Called on every successful fetchMissions().
 * Stores each WO individually so single-WO reads are fast.
 */
export const cacheWorkOrders = async (orders) => {
  let db;
  let transaction;
  try {
    db = await openDB();
    transaction = tx(db, 'work_orders', 'readwrite');
    const store = transaction.objectStore('work_orders');
    // RMI-2026-655: this used to `await` every put one after another. Within one
    // transaction IndexedDB already runs requests in order, so queue the
    // clear + every put back-to-back and wait ONCE for the transaction to
    // commit (~2-3x faster in Chromium; measured in the PR). Because it is a
    // single transaction the replace is atomic -- if anything fails (quota,
    // bad record) it all rolls back and the previous cache survives -- and
    // commit-time errors are now caught instead of silently lost.
    const committed = new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('transaction aborted'));
    });
    committed.catch(() => {}); // we may abort below on a synchronous throw; don't surface that as an unhandled rejection
    const cachedAt = Date.now();
    // Clear old cache first so stale WOs don't linger
    store.clear();
    for (const wo of orders) {
      store.put({ ...wo, _cached_at: cachedAt });
    }
    await committed;
  } catch (err) {
    // A record that can't be stored throws synchronously from put() and would
    // otherwise leave the transaction to commit PARTIALLY (cleared + some rows).
    // Abort so the previous cache survives intact.
    if (transaction) { try { transaction.abort(); } catch (_) { /* already finished */ } }
    console.warn('[OfflineCache] cacheWorkOrders failed:', err.message);
  } finally {
    if (db) db.close();
  }
};

/**
 * Read all cached work orders. Returns [] if cache is empty or DB fails.
 */
export const getCachedWorkOrders = async () => {
  try {
    const db    = await openDB();
    const store = tx(db, 'work_orders').objectStore('work_orders');
    const all   = await promisify(store.getAll());
    db.close();
    return all || [];
  } catch (err) {
    console.warn('[OfflineCache] getCachedWorkOrders failed:', err.message);
    return [];
  }
};

/**
 * Upsert a single work order (e.g. after optimistic status update).
 */
export const upsertCachedWorkOrder = async (wo) => {
  try {
    const db    = await openDB();
    const store = tx(db, 'work_orders', 'readwrite').objectStore('work_orders');
    await promisify(store.put({ ...wo, _cached_at: Date.now() }));
    db.close();
  } catch (err) {
    console.warn('[OfflineCache] upsertCachedWorkOrder failed:', err.message);
  }
};

// ── Photo Queue ───────────────────────────────────────────────────────────────

/**
 * Queue a photo for upload when connectivity resumes.
 * @param {object} opts
 *   wo_id         — work order UUID
 *   entity_type   — 'WORK_ORDER' | 'ASSET'
 *   entity_id     — UUID of the entity
 *   blob          — File or Blob object
 *   filename      — string
 *   mime_type     — string
 *   notes         — optional string label
 *   capture_lat   — optional GPS latitude at capture time (RMI-2026-462)
 *   capture_lng   — optional GPS longitude at capture time (RMI-2026-462)
 *   gps_status    — 'CAPTURED' | 'UNAVAILABLE' | 'SKIPPED' (RMI-2026-462)
 *   uploaded_by   — profile id of the uploader (RMI-2026-462)
 *   review_status — 'PENDING' | 'APPROVED' (RMI-2026-462) — matches the
 *                   same willRequireApproval logic a direct online upload
 *                   already computes, so a queued photo isn't silently
 *                   less complete than one uploaded directly.
 */
export const queuePhoto = async ({
  wo_id, entity_type, entity_id, blob, filename, mime_type, notes = '',
  capture_lat = null, capture_lng = null, gps_status = 'SKIPPED',
  uploaded_by = null, review_status = 'PENDING',
}) => {
  try {
    const db    = await openDB();
    const store = tx(db, 'photo_queue', 'readwrite').objectStore('photo_queue');
    const entry = {
      id:          `photo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      wo_id,
      entity_type,
      entity_id,
      blob,         // stored as Blob — IndexedDB handles binary natively
      filename,
      mime_type,
      notes,
      capture_lat,
      capture_lng,
      gps_status,
      uploaded_by,
      review_status,
      queued_at:   new Date().toISOString(),
    };
    await promisify(store.put(entry));
    db.close();
    console.log('[OfflineCache] Photo queued:', filename);
    return entry.id;
  } catch (err) {
    console.error('[OfflineCache] queuePhoto failed:', err.message);
    return null;
  }
};

/**
 * Get all queued photos awaiting upload.
 */
export const getPendingPhotos = async () => {
  try {
    const db    = await openDB();
    const store = tx(db, 'photo_queue').objectStore('photo_queue');
    const all   = await promisify(store.getAll());
    db.close();
    return all || [];
  } catch (err) {
    console.warn('[OfflineCache] getPendingPhotos failed:', err.message);
    return [];
  }
};

/**
 * Remove a photo from the queue after successful upload.
 */
export const clearQueuedPhoto = async (id) => {
  try {
    const db    = await openDB();
    const store = tx(db, 'photo_queue', 'readwrite').objectStore('photo_queue');
    await promisify(store.delete(id));
    db.close();
  } catch (err) {
    console.warn('[OfflineCache] clearQueuedPhoto failed:', err.message);
  }
};

/**
 * Count pending photos (for UI badge).
 */
export const getPendingPhotoCount = async () => {
  try {
    const db    = await openDB();
    const store = tx(db, 'photo_queue').objectStore('photo_queue');
    const count = await promisify(store.count());
    db.close();
    return count || 0;
  } catch (_err) {
    return 0;
  }
};

// ── Note Queue ────────────────────────────────────────────────────────────────

/**
 * Queue a note write for a work order when offline.
 */
export const queueNote = async ({ wo_id, notes }) => {
  try {
    const db    = await openDB();
    const store = tx(db, 'note_queue', 'readwrite').objectStore('note_queue');
    // One entry per WO — overwrite if already queued so latest note wins
    await promisify(store.put({
      id:        `note_${wo_id}`,
      wo_id,
      notes,
      queued_at: new Date().toISOString(),
    }));
    db.close();
    console.log('[OfflineCache] Note queued for WO:', wo_id);
  } catch (err) {
    console.error('[OfflineCache] queueNote failed:', err.message);
  }
};

/**
 * Get all queued notes.
 */
export const getPendingNotes = async () => {
  try {
    const db    = await openDB();
    const store = tx(db, 'note_queue').objectStore('note_queue');
    const all   = await promisify(store.getAll());
    db.close();
    return all || [];
  } catch (_err) {
    return [];
  }
};

/**
 * Remove a note from the queue after successful sync.
 */
export const clearQueuedNote = async (id) => {
  try {
    const db    = await openDB();
    const store = tx(db, 'note_queue', 'readwrite').objectStore('note_queue');
    await promisify(store.delete(id));
    db.close();
  } catch (err) {
    console.warn('[OfflineCache] clearQueuedNote failed:', err.message);
  }
};

// ── Profile Cache ─────────────────────────────────────────────────────────────
// Single-record store keyed 'me' — the signed-in specialist's own profile object
// plus the avatar image bytes (Blob). The avatar lives in the private hr-docs
// bucket and is normally rendered via a short-lived signed URL (which needs
// network); caching the actual bytes lets the mobile Profile modal show the
// photo and all profile fields in a dead zone. RMI-2026-177.

/**
 * Cache the signed-in specialist's profile and (optionally) their avatar bytes.
 * @param {object} opts
 *   profile     — the full profiles_v2 row object
 *   avatarBlob  — optional Blob of the avatar image
 *   avatarPath  — optional hr-docs object path (for reference / cache validation)
 */
export const cacheProfile = async ({ profile, avatarBlob = null, avatarPath = null }) => {
  try {
    const db    = await openDB();
    const store = tx(db, 'profile', 'readwrite').objectStore('profile');
    await promisify(store.put({
      id:          'me',
      profile,
      avatar_blob: avatarBlob,   // Blob | null — IndexedDB stores binary natively
      avatar_path: avatarPath,
      _cached_at:  Date.now(),
    }));
    db.close();
  } catch (err) {
    console.warn('[OfflineCache] cacheProfile failed:', err.message);
  }
};

/**
 * Read the cached profile record. Returns the full record
 * ({ id, profile, avatar_blob, avatar_path, _cached_at }) or null.
 */
export const getCachedProfile = async () => {
  try {
    const db    = await openDB();
    const store = tx(db, 'profile').objectStore('profile');
    const rec   = await promisify(store.get('me'));
    db.close();
    return rec || null;
  } catch (err) {
    console.warn('[OfflineCache] getCachedProfile failed:', err.message);
    return null;
  }
};

// [ END OF MODULE - VER 1.3 (src/utils/OfflineCacheService.js) ]
/*
=========================================
CHANGE LOG
=========================================
VER 1.3 — 2026-10-02 — BUILD-2026.1036 — RMI-2026-655
  - cacheWorkOrders: clear + all puts are queued back-to-back in ONE transaction and
    awaited once (oncomplete) instead of awaiting every put. ~2-3x faster in Chromium
    (20 WOs 1.7 -> 0.8 ms, 100 WOs 6.2 -> 3.2 ms, 500 WOs 31.9 -> ~11 ms). Replace is now
    atomic and commit/quota errors are caught; the DB is closed in `finally`
    (it used to leak open on error). `_cached_at` is one timestamp per batch.
VER 1.1 — 2026-06-20 — RMI-2026-177
  - Added a 'profile' object store (DB_VERSION 1 -> 2, additive upgrade — the
    three existing stores are preserved by their `if (!contains)` guards).
  - cacheProfile({ profile, avatarBlob, avatarPath }) / getCachedProfile():
    single-record ('me') cache of the signed-in specialist profile + avatar
    Blob, so the mobile Profile modal renders fully offline.
VER 1.0 — Initial: work_orders cache, photo_queue, note_queue.
=========================================
*/
